import { DeliverableLink } from "../types/deliverable";
import { ContentSubmissionUnit } from "../types/content";
import { Collab } from "../types/collab";

/**
 * A brand can void an individual deliverable slot from the dashboard: the creator no longer owes it.
 *
 * `status: 'voided'` is the single source of truth — see `DeliverableStatus` for why the void is a
 * status rather than an additive flag. `voidedAt`/`voidedBy`/`voidReason` are audit only, and must
 * never be what a reader tests.
 *
 * Voided slots are never removed from `deliverableLinks[platform]` — the record stays for audit, and
 * every write path here addresses a slot by its positional `linkIndex`, so compacting the array would
 * redirect a creator's submission into the wrong slot. Filter at read; never rebuild.
 */

/** Kept structurally identical to the dashboard's `collabDeliverableUtils` so the two repos cannot drift. */
export const isVoided = (slot: { status?: string } | undefined): boolean =>
  slot?.status === 'voided';

/**
 * True when nobody is waiting on this deliverable any more — delivered and approved, or written off
 * by the brand. The counterpart of "still owed", and what "is this collab finished?" must ask.
 */
export const isDeliverableSettled = (status?: string): boolean =>
  status === 'approved' || status === 'voided';

/**
 * The links a creator still has a relationship with, per platform. For callers that only ask
 * questions of the set (`some`/`every`/counts) and never index into it — anything that needs the real
 * array position must filter in place and keep its own index.
 */
export const activeLinks = (
  deliverableLinks: Record<string, DeliverableLink[]> | undefined,
): DeliverableLink[] =>
  Object.values(deliverableLinks ?? {})
    .flat()
    .filter((link) => !!link && !isVoided(link));

/** The same, for UGC submission units keyed by requirement. */
export const activeUnits = (
  content: Record<string, ContentSubmissionUnit> | undefined,
): ContentSubmissionUnit[] =>
  Object.values(content ?? {}).filter((unit) => !!unit && !isVoided(unit));

